/** Global balance constants. The single home for tuning knobs. */
export const BALANCE = {
  /** Logic ticks per second (fixed timestep). */
  tickRate: 10,
  get tickMs(): number {
    return 1000 / this.tickRate;
  },
  /** Clamp on how much real time a single frame may advance the sim. */
  maxFrameMs: 250,
  /** Maximum offline time credited, in ms (8 hours). */
  offlineCapMs: 8 * 60 * 60 * 1000,
  /** Offline production is credited at this efficiency. */
  offlineEfficiency: 0.6,

  /** Starting money. */
  startMoney: 50,

  /** Energy throttle: below 1 means production scales by available/required. */
  minThrottle: 0,

  /** Mining efficiency penalty when tool level is below the layer requirement. */
  underToolPenalty: 0.12,

  /** Prestige currency: floor(K * (lifetimeEarnings / scale)^exp). */
  prestige: {
    K: 1,
    scale: 1e6,
    exp: 0.5,
    /** Each prestige point grants this much global multiplier. */
    pointMult: 0.015,
    /** Minimum lifetime earnings before prestige is allowed. */
    minLifetime: 1e6,
  },

  /** Market mean-reverting random-walk parameters. */
  market: {
    reversion: 0.05,
    volatility: 0.04,
    minFactor: 0.3,
    maxFactor: 3.0,
    /** Seconds between price updates. */
    updateInterval: 3,
  },

  /** Random events. */
  events: {
    /** Average seconds between event rolls once unlocked. */
    rollInterval: 45,
    /** Probability an event fires on a roll. */
    chance: 0.35,
    /** Cooldown after an event ends, in seconds. */
    cooldown: 30,
    /** Lifetime-earnings threshold before events begin. */
    unlockLifetime: 5e3,
  },

  /** Quest/contract system. */
  quests: {
    maxActive: 3,
    /** Lifetime-earnings threshold before quests begin. */
    unlockLifetime: 1e3,
  },

  /** Autosave interval in ms. */
  autosaveMs: 20_000,

  /** Smelting: ingots are worth this multiple of the raw ore value. */
  ingotValueMult: 4,

  /** Click combo: consecutive clicks within the window build a click multiplier. */
  combo: {
    window: 2000, // ms since last click before the combo resets
    step: 0.04, // bonus added per stacked click
    maxBonus: 4, // cap on the bonus (×5 total at max)
  },

  /** Activatable abilities (cooldown boosts). */
  abilities: {
    unlockLifetime: 2_000,
  },

  /** Golden nuggets: random clickable bonus. */
  nuggets: {
    unlockLifetime: 1e4,
    chance: 0.5, // probability a roll spawns a nugget
    rollInterval: 30, // seconds between spawn rolls
    minGap: 25, // min seconds before the next roll after one resolves
    maxGap: 75,
    ttl: 12, // seconds a nugget stays clickable
    cashFactor: 90, // cash reward ≈ this many seconds of current income
  },

  /** Ascension: the second prestige layer. */
  ascension: {
    Kc: 1,
    scaleC: 500,
    exp: 0.5,
    coreMult: 0.1, // global multiplier per core
    minPrestigePoints: 500, // lifetime prestige points required to ascend
  },
} as const;

/** Default RNG seed used for new games when none supplied. */
export const DEFAULT_SEED = 0x1f2e3d4c;
